import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Bell, Plus, Play, Trash2, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

/**
 * AdminEmailAlertsTile — CRUD for `email_alert_rules`.
 *
 * Each rule watches one `template_key` from the `email_deliveries`
 * collection over a rolling window and pings the configured Slack
 * webhook when the metric (delivery_rate / bounce_rate / failure_rate)
 * breaches its threshold. The scheduler evaluates every 30 minutes;
 * admins can also click "Run now" to test immediately.
 */
const METRICS = [
  { value: 'delivery_rate', label: 'Delivery % below threshold' },
  { value: 'bounce_rate', label: 'Bounce % above threshold' },
  { value: 'failure_rate', label: 'Failure % above threshold' },
];

const empty = {
  template_key: '',
  metric: 'delivery_rate',
  threshold_pct: 90,
  window_hours: 24,
  min_volume: 5,
  slack_webhook: '',
  enabled: true,
  cooldown_hours: 12,
};

const AdminEmailAlertsTile = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty);

  const load = async () => {
    setLoading(true);
    const { data } = await apiFetch('/api/admin/email-alerts');
    setRules(data?.rules || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!draft.template_key.trim()) {
      toast.error('Template key is required');
      return;
    }
    if (!draft.slack_webhook.startsWith('https://hooks.slack.com/')) {
      toast.error('Slack webhook must start with https://hooks.slack.com/');
      return;
    }
    setCreating(true);
    const { data, error } = await apiFetch('/api/admin/email-alerts', {
      method: 'POST',
      body: JSON.stringify(draft),
    });
    setCreating(false);
    if (error) {
      toast.error(error?.detail || 'Failed to create rule');
      return;
    }
    toast.success('Alert rule created');
    setDraft(empty);
    setRules((prev) => [...prev, data]);
  };

  const onToggle = async (rule_id, enabled) => {
    await apiFetch(`/api/admin/email-alerts/${rule_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
    setRules((prev) => prev.map((r) => r.rule_id === rule_id ? { ...r, enabled } : r));
  };

  const onDelete = async (rule_id) => {
    if (!window.confirm('Delete this alert rule?')) return;
    await apiFetch(`/api/admin/email-alerts/${rule_id}`, { method: 'DELETE' });
    setRules((prev) => prev.filter((r) => r.rule_id !== rule_id));
    toast.success('Rule deleted');
  };

  const onRunNow = async () => {
    const { data, error } = await apiFetch('/api/admin/email-alerts/run-now', { method: 'POST' });
    if (error) {
      toast.error('Run failed');
      return;
    }
    const firedCount = (data?.fired || []).length;
    toast.success(firedCount > 0
      ? `Evaluated · ${firedCount} alert(s) fired`
      : 'Evaluated · no breaches'
    );
    load();
  };

  return (
    <Card data-testid="admin-email-alerts-tile" className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-amber-500" />
            Email deliverability alerts
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={onRunNow}
            data-testid="email-alerts-run-now"
          >
            <Play className="h-3 w-3 mr-1" /> Run now
          </Button>
        </CardTitle>
        <CardDescription className="text-xs">
          Pings a Slack webhook when a template's delivery/bounce/failure rate
          breaches its threshold. Scheduler runs every 30 minutes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing rules */}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rules.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="email-alerts-empty">
            No alert rules yet. Add one below to start monitoring.
          </p>
        ) : (
          <div className="space-y-2" data-testid="email-alerts-list">
            {rules.map((r) => (
              <div
                key={r.rule_id}
                className="rounded border p-3 flex items-center justify-between gap-2 text-xs"
                data-testid={`alert-rule-${r.rule_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] truncate">{r.template_key}</div>
                  <div className="text-muted-foreground">
                    {r.metric.replace('_', ' ')} · threshold {r.threshold_pct}% · window {r.window_hours}h
                  </div>
                  {r.last_fired_at && (
                    <Badge variant="destructive" className="mt-1 text-[10px]">
                      <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                      Last fired {new Date(r.last_fired_at).toLocaleString()}
                    </Badge>
                  )}
                </div>
                <Switch
                  checked={!!r.enabled}
                  onCheckedChange={(v) => onToggle(r.rule_id, v)}
                  data-testid={`alert-toggle-${r.rule_id}`}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onDelete(r.rule_id)}
                  data-testid={`alert-delete-${r.rule_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* New-rule form */}
        <div className="rounded border bg-muted/20 p-3 space-y-3" data-testid="email-alerts-new-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Template key</Label>
              <Input
                value={draft.template_key}
                onChange={(e) => setDraft({ ...draft, template_key: e.target.value })}
                placeholder="welcome / send_back / digest"
                data-testid="alert-template-key-input"
              />
            </div>
            <div>
              <Label className="text-[11px]">Metric</Label>
              <Select
                value={draft.metric}
                onValueChange={(v) => setDraft({ ...draft, metric: v })}
              >
                <SelectTrigger data-testid="alert-metric-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METRICS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Threshold %</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={draft.threshold_pct}
                onChange={(e) => setDraft({ ...draft, threshold_pct: Number(e.target.value) })}
                data-testid="alert-threshold-input"
              />
            </div>
            <div>
              <Label className="text-[11px]">Window (hours)</Label>
              <Input
                type="number"
                min={1}
                value={draft.window_hours}
                onChange={(e) => setDraft({ ...draft, window_hours: Number(e.target.value) })}
                data-testid="alert-window-input"
              />
            </div>
            <div>
              <Label className="text-[11px]">Min volume</Label>
              <Input
                type="number"
                min={1}
                value={draft.min_volume}
                onChange={(e) => setDraft({ ...draft, min_volume: Number(e.target.value) })}
                data-testid="alert-min-volume-input"
              />
            </div>
            <div>
              <Label className="text-[11px]">Cooldown (hours)</Label>
              <Input
                type="number"
                min={0}
                value={draft.cooldown_hours}
                onChange={(e) => setDraft({ ...draft, cooldown_hours: Number(e.target.value) })}
                data-testid="alert-cooldown-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Slack webhook URL</Label>
            <Input
              value={draft.slack_webhook}
              onChange={(e) => setDraft({ ...draft, slack_webhook: e.target.value })}
              placeholder="https://hooks.slack.com/services/..."
              data-testid="alert-webhook-input"
            />
          </div>
          <Button
            size="sm"
            onClick={onCreate}
            disabled={creating}
            data-testid="alert-create-btn"
            className="bg-[#00A896] hover:bg-[#008B7A] text-white"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            {creating ? 'Creating…' : 'Add alert rule'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminEmailAlertsTile;
